import {
  LayoutDashboard,
  Receipt,
  Wallet,
  FileText,
  BookOpen,
  Landmark,
  Leaf,
  Settings,
  Users,
  ChevronDown,
  Shield,
  Globe,
  CreditCard,
  UserPlus,
  Link2,
  PenSquare,
  ClipboardList,
} from "lucide-react";
import vlePlusLogo from "@/assets/logo.jpeg";
import { NavLink } from "@/components/NavLink";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

// Tool keys matching VLE_TOOLS
const TOOL_KEYS = {
  KIOSK_BANKING: "kiosk_banking",
  PIK_VIMA: "pik_vima",
  DAILY_BOOK: "daily_book",
} as const;

export function AppSidebar() {
  const { t } = useLanguage();
  const { tenant, isPlatformAdmin } = useAuth();
  const { state } = useSidebar();
  const location = useLocation();
  const collapsed = state === "collapsed";
  
  // Fetch pending approvals count for platform admins
  const { data: pendingCount = 0 } = useQuery({
    queryKey: ["pending-approvals-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("vle_custom_services")
        .select("*", { count: "exact", head: true })
        .eq("is_approved", false)
        .is("rejection_reason", null);
      
      if (error) return 0;
      return count || 0;
    },
    enabled: isPlatformAdmin,
    refetchInterval: 30000,
  });

  // Fetch all VLE tools status for this tenant
  const { data: vleTools } = useQuery({
    queryKey: ["vle_tools_sidebar", tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return {};
      
      const { data, error } = await supabase
        .from("vle_tools")
        .select("tool_key, is_enabled")
        .eq("tenant_id", tenant.id);
      
      if (error) return {};
      
      // Convert to map
      const toolsMap: Record<string, boolean> = {};
      data?.forEach((tool) => {
        toolsMap[tool.tool_key] = tool.is_enabled;
      });
      return toolsMap;
    },
    enabled: !!tenant?.id,
    staleTime: 30000,
  });

  // Check if specific tool is enabled
  const isToolEnabled = (toolKey: string): boolean => {
    // Default: Daily Book is always enabled, others are disabled by default
    if (toolKey === TOOL_KEYS.DAILY_BOOK) {
      return vleTools?.[toolKey] !== false;
    }
    return vleTools?.[toolKey] ?? false;
  };

  // Tool visibility
  const showKioskBanking = isToolEnabled(TOOL_KEYS.KIOSK_BANKING);
  const showPikVima = isToolEnabled(TOOL_KEYS.PIK_VIMA);
  const showDailyBook = isToolEnabled(TOOL_KEYS.DAILY_BOOK);
  
  // Check current mode based on route
  const isPlatformMode = location.pathname.startsWith('/platform');
  const isBusinessSettingsMode = location.pathname.startsWith('/business');

  // Main Navigation (always visible)
  const mainNavItems = [
    { title: t('dashboard'), url: '/dashboard', icon: LayoutDashboard },
    { title: t('sales'), url: '/sales', icon: Receipt },
    { title: t('expenses'), url: '/expenses', icon: Wallet },
    { title: t('reports'), url: '/reports', icon: FileText },
  ];

  // Books items - filtered by tool status
  const booksNavItems = [
    ...(showDailyBook ? [{ title: t('daily_book'), url: '/daily-book', icon: BookOpen }] : []),
    ...(showKioskBanking ? [{ title: t('kiosk_book'), url: '/kiosk-book', icon: Landmark }] : []),
  ];

  // Management items - filtered by tool status
  const managementNavItems = [
    ...(showPikVima ? [{ title: t('pik_vima'), url: '/pik-vima', icon: Leaf }] : []),
    { title: t('services'), url: '/services', icon: Receipt },
    { title: t('customers'), url: '/customers', icon: Users },
    { title: t('settings'), url: '/settings', icon: Settings },
  ];

  // Setu Suvidha items (customer-facing services)
  const setuSuvidhaNavItems = [
    { title: 'Doc Slip', url: '/setu-suvidha/doc-slip', icon: ClipboardList },
  ];

  // Setu Management items (backend/admin)
  const setuManagementNavItems = [
    { title: 'Doc Slip', url: '/document-slip', icon: ClipboardList },
  ];

  // Business Settings Navigation (VLE owner)
  const businessSettingsNavItems = [
    { title: 'Business Overview', url: '/business/dashboard', icon: Shield },
    { title: 'Subscription', url: '/business/subscription', icon: CreditCard },
    { title: 'Affiliate Program', url: '/business/affiliate', icon: Link2 },
    { title: 'Tools & Utilities', url: '/business/tools', icon: Settings },
    { title: 'Business Settings', url: '/business/settings', icon: Settings },
  ];

  // Platform Admin Navigation
  const platformNavItems = [
    { title: 'Platform Dashboard', url: '/platform/dashboard', icon: Globe, badge: null },
    { title: 'Tenant Management', url: '/platform/tenants', icon: Shield, badge: null },
    { title: 'Subscriptions', url: '/platform/subscriptions', icon: CreditCard, badge: null },
    { title: 'Affiliate Management', url: '/platform/affiliates', icon: UserPlus, badge: null },
    { title: 'Blog Management', url: '/platform/blog', icon: PenSquare, badge: null },
    { title: 'Platform Settings', url: '/platform/settings', icon: Settings, badge: pendingCount > 0 ? pendingCount : null },
  ];

  // Render Platform Admin Sidebar
  if (isPlatformMode) {
    return (
      <Sidebar collapsible="icon" className="border-r border-sidebar-border w-64">
        <SidebarHeader className="border-b border-sidebar-border px-4 py-4">
          <div className="flex items-center gap-3">
            <img src={vlePlusLogo} alt="VLE Plus Logo" className="h-10 w-10 rounded-lg object-contain" />
            {!collapsed && (
              <div className="flex flex-col gap-0.5">
                <span className="text-base font-semibold text-sidebar-foreground">
                  VLE Plus
                </span>
                <Badge variant="destructive" className="text-xs px-1.5 py-0 w-fit">
                  Platform Admin
                </Badge>
              </div>
            )}
          </div>
        </SidebarHeader>

        <SidebarContent className="px-3">
          <SidebarGroup>
            <SidebarGroupLabel className="text-sm font-medium text-muted-foreground tracking-wide mb-1">
              {!collapsed && 'Platform Management'}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="space-y-1">
                {platformNavItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                        activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                      >
                        <item.icon className="h-5 w-5 shrink-0" />
                        {!collapsed && (
                          <span className="flex-1 flex items-center justify-between">
                            {item.title}
                            {item.badge && (
                              <Badge variant="destructive" className="ml-2 h-5 min-w-5 px-1.5 text-xs">
                                {item.badge}
                              </Badge>
                            )}
                          </span>
                        )}
                        {collapsed && item.badge && (
                          <Badge variant="destructive" className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px]">
                            {item.badge}
                          </Badge>
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>


          {/* Back to App Link */}
          {tenant && (
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <Link
                        to="/dashboard"
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                      >
                        <LayoutDashboard className="h-5 w-5 shrink-0" />
                        {!collapsed && <span>Back to App</span>}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </SidebarContent>

        <SidebarFooter className="border-t border-sidebar-border p-3">
          <div className={cn(
            "flex items-center gap-2 rounded-lg p-2 text-sm text-muted-foreground",
            collapsed && "justify-center"
          )}>
            {!collapsed && (
              <span>Platform Admin Mode</span>
            )}
          </div>
        </SidebarFooter>
      </Sidebar>
    );
  }

  // Render Business Settings Sidebar
  if (isBusinessSettingsMode) {
    return (
      <Sidebar collapsible="icon" className="border-r border-sidebar-border w-64">
        <SidebarHeader className="border-b border-sidebar-border px-4 py-4">
          <div className="flex items-center gap-3">
            <img src={vlePlusLogo} alt="VLE Plus Logo" className="h-10 w-10 rounded-lg object-contain" />
            {!collapsed && (
              <div className="flex flex-col gap-0.5">
                <span className="text-base font-semibold text-sidebar-foreground">
                  {tenant?.name || t('app_name')}
                </span>
                <Badge variant="secondary" className="text-xs px-1.5 py-0 w-fit">
                  Business Settings
                </Badge>
              </div>
            )}
          </div>
        </SidebarHeader>

        <SidebarContent className="px-3">
          <SidebarGroup>
            <SidebarGroupLabel className="text-sm font-medium text-muted-foreground tracking-wide mb-1">
              {!collapsed && 'Business Settings'}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="space-y-1">
                {businessSettingsNavItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                        activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                      >
                        <item.icon className="h-5 w-5 shrink-0" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Back to Billing Link */}
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link
                      to="/dashboard"
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                    >
                      <LayoutDashboard className="h-5 w-5 shrink-0" />
                      {!collapsed && <span>Back to Billing</span>}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="border-t border-sidebar-border p-3">
          <div className={cn(
            "flex items-center gap-2 rounded-lg p-2 text-sm text-muted-foreground",
            collapsed && "justify-center"
          )}>
            {!collapsed && (
              <span>© {new Date().getFullYear()} VLE Plus</span>
            )}
          </div>
        </SidebarFooter>
      </Sidebar>
    );
  }

  // Render Default Billing Sidebar (VLE mode)
  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border w-64">
      <SidebarHeader className="border-b border-sidebar-border px-4 py-4">
        <div className="flex items-center gap-3">
          <img src={vlePlusLogo} alt="VLE Plus Logo" className="h-10 w-10 rounded-lg object-contain" />
          {!collapsed && (
            <div className="flex flex-col gap-0.5">
              <span className="text-base font-semibold text-sidebar-foreground">
                {tenant?.name || t('app_name')}
              </span>
              <span className="text-sm text-muted-foreground">
                {tenant?.business_type || 'VLE'}
              </span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="px-3">
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-sm font-medium text-muted-foreground tracking-wide mb-1">
            {!collapsed && 'Main'}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {mainNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                      activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Books - Only show if there are enabled book tools */}
        {booksNavItems.length > 0 && (
          <SidebarGroup>
            <Collapsible defaultOpen className="group/collapsible">
              <CollapsibleTrigger asChild>
                <SidebarGroupLabel className="flex cursor-pointer items-center justify-between text-sm font-medium text-muted-foreground hover:text-foreground tracking-wide mb-1">
                  {!collapsed && 'Books'}
                  <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                </SidebarGroupLabel>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu className="space-y-1">
                    {booksNavItems.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild>
                          <NavLink
                            to={item.url}
                            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                            activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                          >
                            <item.icon className="h-5 w-5 shrink-0" />
                            {!collapsed && <span>{item.title}</span>}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </Collapsible>
          </SidebarGroup>
        )}

        {/* Management */}
        <SidebarGroup>
          <Collapsible defaultOpen className="group/collapsible">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className="flex cursor-pointer items-center justify-between text-sm font-medium text-muted-foreground hover:text-foreground tracking-wide mb-1">
                {!collapsed && 'Management'}
                <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu className="space-y-1">
                  {managementNavItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={item.url}
                          className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                          activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                        >
                          <item.icon className="h-5 w-5 shrink-0" />
                          {!collapsed && <span>{item.title}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </Collapsible>
        </SidebarGroup>

        {/* Setu Suvidha */}
        <SidebarGroup>
          <Collapsible defaultOpen className="group/collapsible">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className="flex cursor-pointer items-center justify-between text-sm font-medium text-muted-foreground hover:text-foreground tracking-wide mb-1">
                {!collapsed && 'Setu Suvidha'}
                <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu className="space-y-1">
                  {setuSuvidhaNavItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={item.url}
                          className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                          activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                        >
                          <item.icon className="h-5 w-5 shrink-0" />
                          {!collapsed && <span>{item.title}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </Collapsible>
        </SidebarGroup>

        {/* Setu Management */}
        <SidebarGroup>
          <Collapsible defaultOpen className="group/collapsible">
            <CollapsibleTrigger asChild>
              <SidebarGroupLabel className="flex cursor-pointer items-center justify-between text-sm font-medium text-muted-foreground hover:text-foreground tracking-wide mb-1">
                {!collapsed && 'Setu Management'}
                <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </SidebarGroupLabel>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu className="space-y-1">
                  {setuManagementNavItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={item.url}
                          className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
                          activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                        >
                          <item.icon className="h-5 w-5 shrink-0" />
                          {!collapsed && <span>{item.title}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </Collapsible>
        </SidebarGroup>

        {/* Business Settings Link */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink
                    to="/business/dashboard"
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                    activeClassName="bg-primary text-primary-foreground font-medium"
                  >
                    <Shield className="h-5 w-5 shrink-0" />
                    {!collapsed && <span>Business Settings</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Platform Admin Link - ONLY visible to platform_admin */}
        {isPlatformAdmin && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to="/platform/dashboard"
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-base bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
                      activeClassName="bg-destructive text-destructive-foreground font-medium"
                    >
                      <Globe className="h-5 w-5 shrink-0" />
                      {!collapsed && <span>Platform Admin</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-3">
        <div className={cn(
          "flex items-center gap-2 rounded-lg p-2 text-sm text-muted-foreground",
          collapsed && "justify-center"
        )}>
          {!collapsed && (
            <span>© {new Date().getFullYear()} VLE Plus</span>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
